import React from "react";
import { Button } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiFileDocumentOutline,
  mdiPencilOutline,
  mdiTrayArrowDown,
} from "@mdi/js";
import CardShell from "./CardShell";
import SendButton from "./SendButton";
import JobStatusBadge from "app/components/common/JobStatusBadge";
import {
  formatTimestampDate,
  formatTimestampDateTime,
} from "app/containers/Jobs/utils/dateFormat";
import {
  CONTRACT_STATE,
  hasSignedCopy,
  needsFreshContract,
} from "app/utils/contractState";
import { revokeReasonText } from "app/containers/Jobs/utils/contractReasons";

// declined / delivery_failed offer neither Revoke nor Re-issue: the backend 409s
// contract.invalid_state on both, and a fresh POST /contracts/ is the only
// recovery — which is the same send the pristine card fires.
//
// Download is gated on the contract having a final signed PDF (completed_at),
// not on the status, matching the backend's own download gate — so a reset or a
// void of an already-signed contract can still fetch the copy the client holds.
const ContractCard = ({
  job,
  onEditTemplate,
  onSendContract,
  onReissue,
  onRevoke,
  onResetStatus,
  onDownload,
  onPreviewTemplate,
  onPreviewContract,
  onUpgrade,
  busy = false,
  sendTrack = {},
  sending = false,
  // The account default, resolved from the real contract templates. There is no
  // per-job contract template before a send — the backend falls back to the
  // user's is_default row — so this must be the real name, never a placeholder.
  defaultTemplateName = null,
  // The computed system template's name. A system-template send returns
  // template_name: null, so without this the card renders an empty heading.
  systemTemplateName = null,
  isFreeTier = false,
  timeZone,
}) => {
  const contract = job.contract || {};
  const status = contract.status || CONTRACT_STATE.NO_CONTRACT;
  const templateName =
    contract.template_name || systemTemplateName || defaultTemplateName;

  const shell = (body, props) => (
    <CardShell
      icon={
        <Icon
          path={mdiFileDocumentOutline}
          size={0.7}
          className="jd-card-icon"
        />
      }
      title="Contract"
      className="jd-card--half jd-card--contract"
      {...props}
    >
      {body}
      {/* A send blocked for a reason fixed elsewhere (no mailbox) keeps that
          reason on the card, not only in the disabled button's tooltip. */}
      {sendTrack.show && sendTrack.alert && (
        <p className="jd-send-alert" role="alert">
          {sendTrack.alert}
        </p>
      )}
    </CardShell>
  );

  const note = (children) => (
    <span className="jd-card-footer-note">{children}</span>
  );

  // Free tier never reaches a contract state — the backend omits the block
  // entirely — so this replaces the card rather than disabling its actions.
  if (isFreeTier) {
    return shell(
      <div className="jd-muted-heading">Send it with every job</div>,
      {
        headerRight: <span className="jd-chip jd-chip--info">Solo</span>,
        footerRight: (
          <Button variant="primary" size="sm" onClick={onUpgrade}>
            Upgrade to Solo
          </Button>
        ),
      }
    );
  }

  const badge = <JobStatusBadge domain="contract" value={status} />;

  // Preview means two different things depending on the state, and each has its
  // own endpoint:
  //   a send is the next action -> the TEMPLATE that would go out
  //                                (templates/{id}/preview-pdf/), so the
  //                                inspector can read it before sending
  //   unsigned                   -> the contract that WAS sent, re-rendered
  //                                 from Contract.content_snapshot
  //                                 (contracts/{id}/preview/). Nothing is
  //                                 sendable here, and Download 404s
  //                                 contract.no_signed_pdf until the client
  //                                 signs, so this is the only way to see it.
  const previewButton = (onClick) => (
    <Button
      variant="outline-primary"
      size="sm"
      disabled={busy}
      onClick={onClick}
    >
      Preview
    </Button>
  );
  // Offered wherever the backend would serve the PDF, i.e. whenever a final
  // signed document was generated.
  const downloadButton = hasSignedCopy(contract) ? (
    <Button
      variant="outline-primary"
      size="sm"
      disabled={busy}
      onClick={onDownload}
    >
      <Icon path={mdiTrayArrowDown} size={0.6} className="me-1" />
      Download
    </Button>
  ) : null;

  const reissueButton = (
    <Button variant="primary" size="sm" disabled={busy} onClick={onReissue}>
      Re-issue Contract
    </Button>
  );

  if (status === "unsigned") {
    return shell(<div className="jd-value-strong">{templateName}</div>, {
      headerRight: badge,
      footerLeft: note(
        <>
          Contract created
          <br />
          Waiting for client
        </>
      ),
      footerRight: (
        <>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={busy}
            onClick={onRevoke}
          >
            Revoke Contract
          </Button>
          {previewButton(onPreviewContract)}
        </>
      ),
    });
  }

  if (status === "signed") {
    return shell(<div className="jd-value-strong">{templateName}</div>, {
      headerRight: badge,
      // completed_at can be set while signed_at is null (a dropped Signed
      // webhook, or the hourly reconciliation path).
      footerLeft: note(
        formatTimestampDateTime(
          contract.signed_at || contract.completed_at,
          timeZone
        )
      ),
      footerRight: (
        <>
          <Button
            variant="link"
            size="sm"
            className="jd-danger-link"
            disabled={busy}
            onClick={onResetStatus}
          >
            Reset Status
          </Button>
          {downloadButton}
        </>
      ),
    });
  }

  // Re-issue is OPTIONAL here — nothing the client signed exists yet, so the
  // reason is the headline rather than a warning.
  if (status === "revoked") {
    return shell(
      <div className="jd-value-strong">{revokeReasonText(contract)}</div>,
      {
        headerRight: badge,
        footerLeft: note(
          `Voided ${formatTimestampDate(contract.voided_at, timeZone)}`
        ),
        footerRight: (
          <>
            {previewButton(onPreviewTemplate)}
            {reissueButton}
          </>
        ),
      }
    );
  }

  // Re-issue is REQUIRED — the signed PDF still exists at BoldSign and in the
  // client's inbox, so doing nothing leaves the client on the wrong agreement.
  if (status === CONTRACT_STATE.REISSUE_REQUIRED) {
    return shell(
      <div className="jd-value-strong jd-value-danger">
        Job changes aren&apos;t covered
      </div>,
      {
        headerRight: badge,
        footerLeft: note("Re-issue to stay protected"),
        footerRight: (
          <>
            {downloadButton}
            {previewButton(onPreviewTemplate)}
            {reissueButton}
          </>
        ),
      }
    );
  }

  // Revoke AND reissue both 409 contract.invalid_state here, so this is the
  // pristine card with a reason: explain what happened, then send a new one.
  if (needsFreshContract(status)) {
    const declined = status === CONTRACT_STATE.DECLINED;
    return shell(
      <div className="jd-value-strong jd-value-danger">
        {declined
          ? "Client declined the contract"
          : "We couldn't deliver the contract"}
      </div>,
      {
        headerRight: badge,
        footerLeft: note(
          declined
            ? "Send a new contract to continue"
            : "Check the client's email, then send again"
        ),
        footerRight: (
          <>
            {previewButton(onPreviewTemplate)}
            <SendButton
              track={sendTrack}
              sending={sending}
              onClick={onSendContract}
            >
              Send Contract
            </SendButton>
          </>
        ),
      }
    );
  }

  return shell(<div className="jd-muted-heading">No contract yet</div>, {
    headerRight: badge,
    footerLeft:
      defaultTemplateName &&
      note(
        <>
          Default: {defaultTemplateName}
          {/* The contract template is a Job Setup field, so this opens Setup. */}
          <Icon
            path={mdiPencilOutline}
            size={0.5}
            className="jd-footer-edit"
            onClick={onEditTemplate}
          />
        </>
      ),
    footerRight: (
      <>
        {previewButton(onPreviewTemplate)}
        <SendButton
          track={sendTrack}
          sending={sending}
          onClick={onSendContract}
        >
          Send Contract
        </SendButton>
      </>
    ),
  });
};

export default ContractCard;
